import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useThemeMode } from "../../contexts/theme-mode";

const WeekNavigation = ({
  onDateChange,
  calendarRef,
  onRegisterClick,
  onViewChange,
  timeZone,
}) => {
  const { isDark } = useThemeMode();
  const [selectedWeeks, setSelectedWeeks] = useState(1);

  const toSafeWallClock = (d) => {
    if (!timeZone) return d.toDate();
    // Land on the correct calendar day in the given timezone (avoid DST edges).
    return dayjs.tz(d.format("YYYY-MM-DD") + " 12:00:00", timeZone).toDate();
  };

  const syncScheduler = (nextDate, nextView) => {
    onDateChange(nextDate);
    if (typeof onViewChange === "function" && nextView) onViewChange(nextView);

    if (calendarRef && calendarRef.current) {
      try {
        calendarRef.current.scheduler?.handleState(nextDate, "selectedDate");
      } catch (error) {
        console.log("Could not update scheduler directly:", error);
      }
      if (nextView) {
        try {
          calendarRef.current.scheduler?.handleState(nextView, "view");
        } catch {
          /* ignore */
        }
      }
    }
  };

  const handleGoToWeek = () => {
    const targetDate = dayjs().add(selectedWeeks, "week");
    console.log(
      "WeekNavigation: Navigating to",
      targetDate.format("YYYY-MM-DD"),
      "from today"
    );

    syncScheduler(toSafeWallClock(targetDate));
  };

  const handleGoToToday = () => {
    const today = dayjs();
    // "Today" always returns user to Day view on today's date.
    syncScheduler(toSafeWallClock(today), "day");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        height: "40px",
        flexWrap: "wrap",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: "medium", whiteSpace: "nowrap" }}>
        Jump to:
      </Typography>
      
      <FormControl size="small" sx={{ minWidth: 60 }}>
        <Select
          value={selectedWeeks}
          onChange={(e) => setSelectedWeeks(e.target.value)}
          displayEmpty
          sx={{
            height: "28px",
            minHeight: "28px",
            ...(isDark && {
              color: "#f2f2f2",
              bgcolor: "#1b1b1e",
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.23)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.4)",
              },
              "& .MuiSvgIcon-root": { color: "#f2f2f2" },
            }),
          }}
          MenuProps={{
            PaperProps: {
              sx: isDark
                ? {
                    bgcolor: "#1b1b1e",
                    color: "#f2f2f2",
                    border: "1px solid #3a3a40",
                    "& .MuiMenuItem-root": {
                      color: "#f2f2f2",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                      "&.Mui-selected": {
                        bgcolor: "rgba(197,160,111,0.28)",
                        "&:hover": { bgcolor: "rgba(197,160,111,0.36)" },
                      },
                    },
                  }
                : undefined,
            },
          }}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((week) => (
            <MenuItem key={week} value={week}>
              {week}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        week{selectedWeeks > 1 ? "s" : ""} from today
      </Typography>
      
      <Button
        variant="contained"
        size="small"
        onClick={handleGoToWeek}
        sx={{
          minWidth: "50px",
          height: "32px",
          borderRadius: "6px",
          boxShadow: "none",
          bgcolor: isDark ? "#ffffff" : "#000000",
          color: isDark ? "#000000" : "#ffffff",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": {
            bgcolor: isDark ? "#e8e8e8" : "#1a1a1a",
            boxShadow: "none",
          },
        }}
      >
        GO
      </Button>
      <Button
        variant="contained"
        size="small"
        onClick={handleGoToToday}
        sx={{
          minWidth: "60px",
          height: "32px",
          borderRadius: "6px",
          boxShadow: "none",
          bgcolor: "#C5A06F",
          color: "#000000",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": {
            bgcolor: "#b8915f",
            boxShadow: "none",
          },
        }}
      >
        Today
      </Button>
      <Button
        variant="contained"
        size="small"
        onClick={onRegisterClick}
        sx={{
          minWidth: "80px",
          height: "32px",
          borderRadius: "6px",
          boxShadow: "none",
          bgcolor: "#C5A06F",
          color: "#000000",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": {
            bgcolor: "#b8915f",
            boxShadow: "none",
          },
        }}
      >
        Register
      </Button>
    </Box>
  );
};

export default WeekNavigation;
